import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { DataGrid } from "@mui/x-data-grid";
import { Button, Chip } from "@mui/material";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import "../../../components/common/css/datatable.scss";

/**
 * Campaign closings — dual-admin payout/refund workflow (C).
 *
 * The completion cron creates a DRAFT closing per campaign at deadline with
 * the computed outcome: FUNDED (raised >= goal) or FAILED_MINIMUM (the
 * platform's minimum-or-refund promise). One admin initiates, a DIFFERENT
 * admin confirms (two-signature release per the platform disclosures), and
 * only then can the payout (issuer credit) or refund (investor credits) ACH
 * file be generated — downloadable from the ACH Files page.
 */
const API = process.env.REACT_APP_API;

const OUTCOME_COLORS = { FUNDED: "success", FAILED_MINIMUM: "warning" };

function authHeaders() {
  return { "x-access-token": localStorage.getItem("accessToken") };
}

const Closings = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}admin/closings`, { headers: authHeaders() });
      setRows(res?.data?.closings || []);
    } catch (error) {
      Swal.fire({
        title: error?.response?.data?.message || "Failed to load closings",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showWorksheet = async (row) => {
    try {
      const res = await axios.get(`${API}admin/closings/${row.id}/worksheet`, {
        headers: authHeaders(),
      });
      const w = res?.data?.worksheet;
      const html =
        w.outcome === "FUNDED"
          ? `<div style="text-align:left">
               <p><b>${w.campaign.name}</b> (${w.campaign.issuer})</p>
               <p>Paid investments: ${w.paidCount}</p>
               <p>Gross: $${w.gross} | Fees (${w.feeRate * 100}%): $${w.fees} | <b>Net payout: $${w.net}</b></p>
               <p>Issuer ACH-ready: ${w.issuerBankAchReady ? "Yes" : "No — manual wire payout"}</p>
             </div>`
          : `<div style="text-align:left">
               <p><b>${w.campaign.name}</b> (${w.campaign.issuer})</p>
               <p>Goal: $${w.goal} | Raised: $${w.raised}</p>
               <p>ACH refunds: $${w.achRefundTotal} | Manual/wire refunds: $${w.manualRefundTotal}</p>
               <p>${w.refunds.length} investor(s) to refund — see the refund file + manual list.</p>
             </div>`;
      Swal.fire({ title: `Worksheet — closing #${row.id}`, html, width: 600 });
    } catch (error) {
      Swal.fire({ title: "Failed to load worksheet", icon: "error", confirmButtonText: "OK" });
    }
  };

  const act = async (row, action, label) => {
    const confirm = await Swal.fire({
      title: label,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
    });
    if (!confirm.isConfirmed) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}admin/closings/${row.id}/${action}`,
        {},
        { headers: authHeaders() }
      );
      if (res?.data?.fileName) {
        Swal.fire({
          title: "File generated",
          text: `${res.data.fileName} — download it from the ACH Files page.`,
          icon: "success",
          confirmButtonText: "OK",
        });
      }
      await fetchRows();
    } catch (error) {
      Swal.fire({
        title: error?.response?.data?.message || "Action failed",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "campaign",
      headerName: "Campaign",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => params.row?.Campaign?.campaign_name || params.row.campaign_id,
    },
    {
      field: "outcome",
      headerName: "Outcome",
      width: 160,
      renderCell: (params) => (
        <Chip label={params.value} color={OUTCOME_COLORS[params.value] || "default"} size="small" />
      ),
    },
    { field: "goal_amount", headerName: "Goal", width: 110 },
    { field: "raised_amount", headerName: "Raised", width: 110 },
    { field: "status", headerName: "Status", width: 190 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 360,
      renderCell: (params) => {
        const c = params.row;
        return (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Button size="small" variant="outlined" onClick={() => showWorksheet(c)}>
              Worksheet
            </Button>
            {c.status === "DRAFT" && (
              <Button size="small" variant="contained" onClick={() => act(c, "initiate", "Initiate this closing (first signature)?")}>
                Initiate
              </Button>
            )}
            {c.status === "PENDING_CONFIRMATION" && (
              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={() => act(c, "confirm", "Confirm this closing (second signature — must be a different admin)?")}
              >
                Confirm
              </Button>
            )}
            {c.status === "CONFIRMED" && c.outcome === "FUNDED" && (
              <Button size="small" color="success" variant="contained" onClick={() => act(c, "generate-payout-file", "Generate the issuer payout ACH credit file?")}>
                Payout file
              </Button>
            )}
            {c.status === "CONFIRMED" && c.outcome === "FAILED_MINIMUM" && (
              <Button size="small" color="warning" variant="contained" onClick={() => act(c, "generate-refund-file", "Generate the investor refund ACH credit file?")}>
                Refund file
              </Button>
            )}
            {["CONFIRMED", "PAYOUT_GENERATED", "REFUNDS_GENERATED"].includes(c.status) && (
              <Button size="small" onClick={() => act(c, "complete", "Mark this closing COMPLETE (funds have moved)?")}>
                Complete
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <PageShell>
      <PageHead title="Campaign closings" />
      <div className="datatable">
        <Loader isLoading={loading} />
        <div className="datatableTitle">
          <h5>Campaign closings (dual-admin payout / refund)</h5>
        </div>
        <DataGrid
          className="datagrid"
          autoHeight
          rows={rows}
          columns={columns}
          hideFooterPagination
        />
      </div>
    </PageShell>
  );
};

export default Closings;
